import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

import { readStored, writeStored } from './storage.ts';

const STORAGE_KEY = 'theme';
const systemQuery = window.matchMedia('(prefers-color-scheme: dark)');

// Stored per browser. Light until someone picks dark or 'system', which follows the operating system, so every page
// (the start page, the sign-in, the app) looks the same for a first visitor.
export type ThemePreference = 'light' | 'dark' | 'system';

const readThemePreference = (): ThemePreference => {
    const stored = readStored(STORAGE_KEY);
    return stored === 'dark' || stored === 'system' ? stored : 'light';
};

const resolveIsDarkMode = (preference: ThemePreference, isSystemDark: boolean) =>
    preference === 'system' ? isSystemDark : preference === 'dark';

export const readIsDarkMode = () => resolveIsDarkMode(readThemePreference(), systemQuery.matches);

const useDarkMode = () => {
    const [themePreference, setThemePreferenceState] = useState(readThemePreference);
    const [isSystemDark, setIsSystemDark] = useState(systemQuery.matches);
    const isDarkMode = resolveIsDarkMode(themePreference, isSystemDark);

    useLayoutEffect(() => {
        // Elements with color transitions would otherwise fade into the new theme while the background switches at
        // once, so the theme changes with transitions disabled for one frame.
        const style = document.createElement('style');
        style.textContent = '*, *::before, *::after { transition: none !important; }';
        document.head.appendChild(style);
        document.documentElement.classList.toggle('dark', isDarkMode);
        // Browsers start transitions from the last rendered frame, so the style stays until a frame with the new
        // colors has been rendered: the first callback runs before that frame, the nested one before the next.
        let frame = requestAnimationFrame(() => {
            frame = requestAnimationFrame(() => style.remove());
        });
        return () => {
            cancelAnimationFrame(frame);
            style.remove();
        };
    }, [isDarkMode]);

    useEffect(() => {
        const refresh = () => setIsSystemDark(systemQuery.matches);
        systemQuery.addEventListener('change', refresh);
        return () => systemQuery.removeEventListener('change', refresh);
    }, []);

    const setThemePreference = useCallback((preference: ThemePreference) => {
        writeStored(STORAGE_KEY, preference === 'light' ? null : preference);
        setThemePreferenceState(preference);
    }, []);

    return { themePreference, setThemePreference, isDarkMode };
};

export default useDarkMode;
